// 手机子页面：记录表按字段展示，比较表保留横向关系；扩大原生按钮触控区域，电脑布局及业务事件不变。
import React, { useEffect, useMemo, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { toast } from "../ui/sonner";
import { Plus, Trash2, Loader2, RotateCw, Search, Building2 } from "lucide-react";

/* 分公司管理（/admin/branches 页面主体）—— 常规列表页
 *
 * 需求背景：
 *   1) 原先「分公司管理」是「账号权限」页顶部按钮堆里的弹窗；已提成独立页（系统管理 → 分公司管理）。
 *   2) 用户走查后要求改成**常规列表页**：顶部放搜索条件与重置，右上角一个「添加分公司」按钮，
 *      点击后**弹窗**输入公司信息 —— 取代最初把输入框直接平铺在列表下方的做法。
 *   3) 去掉页面上的说明性副标题（用户明确不要）。
 *
 * 最终效果：工具栏（搜索 / 重置 / 刷新 / 添加分公司）+ 表格式列表（名称 / 标识 / 操作）+ 空态；
 *   新增走弹窗，删除前确认。数据接口与改造前完全一致（GET / POST / DELETE /branches）。
 *
 * 关键取舍：
 *   - 搜索在前端过滤（分公司总量很小，后端 /branches 无分页），按「中文名 / 英文名 / 标识」模糊匹配。
 *   - 分公司标识（id）由后端自动生成（br_xxxx），弹窗里不暴露，避免用户手填出脏 id。
 *   - 保留既有 data-testid（branch-row-* / branch-del-* / branch-zh / branch-en），新增按钮与工具栏的 testid。
 */
export function BranchManagerPanel({ lang, api }) {
  const zh = lang === "zh";
  const [branches, setBranches] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ zh: "", en: "" });

  const load = async () => {
    setBusy("load");
    try {
      const { data } = await api.get("/branches");
      setBranches(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
      setBranches([]);
    } finally { setBusy(""); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const kw = q.trim().toLowerCase();
  const list = useMemo(() => {
    const all = branches || [];
    if (!kw) return all;
    return all.filter((b) => [b.zh, b.en, b.id].filter(Boolean).some((s) => String(s).toLowerCase().includes(kw)));
  }, [branches, kw]);

  const closeAdd = () => { setAddOpen(false); setForm({ zh: "", en: "" }); };
  const add = async () => {
    if (!form.zh.trim()) { toast.error(zh ? "请输入公司名称" : "Name required"); return; }
    setBusy("add");
    try {
      await api.post("/branches", { zh: form.zh.trim(), en: (form.en || form.zh).trim() });
      toast.success(zh ? "已添加" : "Added");
      closeAdd();
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(""); }
  };
  const del = async (b) => {
    if (!window.confirm((zh ? "删除分公司 " : "Delete branch ") + b.zh + "?")) return;
    setBusy(`del:${b.id}`);
    try { await api.delete(`/branches/${b.id}`); await load(); toast.success(zh ? "已删除" : "Deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-3" data-testid="branch-manager-panel">
      {/* 工具栏：左 搜索 / 重置 / 刷新，右 添加分公司 */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-64">
          <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-slate-400" />
          <Input data-testid="branch-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={zh ? "搜索名称或标识…" : "Search name or id…"} className="h-8 pl-7 text-sm" />
        </div>
        <Button data-testid="branch-reset" variant="outline" size="sm" className="h-8" disabled={!q} onClick={() => setQ("")}>
          {zh ? "重置" : "Reset"}
        </Button>
        <Button data-testid="branch-refresh" variant="outline" size="sm" className="h-8 gap-1" disabled={!!busy} onClick={load}>
          {busy === "load" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />}{zh ? "刷新" : "Refresh"}
        </Button>
        <Button data-testid="branch-add-btn" size="sm" className="h-8 gap-1 ml-auto bg-red-600 hover:bg-red-700 text-white" onClick={() => setAddOpen(true)}>
          <Plus className="h-4 w-4" />{zh ? "添加分公司" : "Add branch"}
        </Button>
      </div>

      {/* 列表：表头 + 行 */}
      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="branch-list">
        <div className="flex items-center gap-3 px-4 py-2 bg-slate-50 text-[11px] font-semibold text-slate-500">
          <span className="flex-1">{zh ? "名称" : "Name"}</span>
          <span className="w-40">{zh ? "标识" : "ID"}</span>
          <span className="w-16 text-right">{zh ? "操作" : "Actions"}</span>
        </div>

        {!branches && <div className="py-8 flex justify-center text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /></div>}
        {branches && list.map((b) => (
          <div key={b.id} data-testid={`branch-row-${b.id}`} className="flex items-center gap-3 px-4 py-2.5 border-t border-slate-100 hover:bg-slate-50/60">
            <span className="flex-1 min-w-0 text-sm text-slate-800 truncate">
              <Building2 className="h-3.5 w-3.5 inline mr-1.5 text-slate-400" />{zh ? b.zh : b.en}
              {zh ? b.en : b.zh ? <span className="ml-1.5 text-xs text-slate-400">{zh ? b.en : b.zh}</span> : null}
            </span>
            <code className="w-40 text-[11px] text-slate-400">{b.id}</code>
            <span className="w-16 text-right">
              <button data-testid={`branch-del-${b.id}`} disabled={busy === `del:${b.id}`}
                onClick={() => del(b)} title={zh ? "删除" : "Delete"}
                className="max-md:min-h-11 max-md:min-w-11 text-slate-400 hover:text-red-600 disabled:opacity-40">
                {busy === `del:${b.id}` ? <Loader2 className="h-4 w-4 inline animate-spin" /> : <Trash2 className="h-4 w-4 inline" />}
              </button>
            </span>
          </div>
        ))}
        {branches && list.length === 0 && (
          <p className="py-8 text-center text-sm text-slate-400" data-testid="branch-empty">
            {kw ? (zh ? "没有匹配的分公司" : "No matching branches") : (zh ? "暂无分公司" : "No branches")}
          </p>
        )}
      </div>

      <p className="text-xs text-slate-400" data-testid="branch-count">
        {zh ? `共 ${list.length} 个分公司${kw ? `（筛选自 ${branches ? branches.length : 0} 个）` : ""}` : `${list.length} branches${kw ? ` (of ${branches ? branches.length : 0})` : ""}`}
      </p>

      {/* 添加分公司弹窗 */}
      <Dialog open={addOpen} onOpenChange={(o) => (o ? setAddOpen(true) : closeAdd())}>
        <DialogContent data-testid="branch-add-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Plus className="h-4 w-4 text-red-600" />{zh ? "添加分公司" : "Add branch"}</DialogTitle>
            <DialogDescription>
              {zh ? "标识（id）由系统自动生成，无需填写；账号的「所属分公司」、项目与客户的数据范围以此为基础。" : "The id is auto-generated. Account branch, project and customer scopes are based on this list."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="block">
              <span className="text-xs text-slate-500">{zh ? "公司名称（中）*" : "Name (ZH) *"}</span>
              <Input data-testid="branch-zh" value={form.zh} autoFocus onChange={(e) => setForm((f) => ({ ...f, zh: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} className="h-9 mt-1" placeholder={zh ? "如 纽约分公司" : "e.g. New York"} />
            </label>
            <label className="block">
              <span className="text-xs text-slate-500">{zh ? "名称（英）" : "Name (EN)"}</span>
              <Input data-testid="branch-en" value={form.en} onChange={(e) => setForm((f) => ({ ...f, en: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} className="h-9 mt-1" placeholder={zh ? "留空则与中文名相同" : "Blank = same as ZH"} />
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" data-testid="branch-cancel" onClick={closeAdd}>{zh ? "取消" : "Cancel"}</Button>
            <Button data-testid="branch-save" disabled={busy === "add" || !form.zh.trim()} onClick={add} className="bg-red-600 hover:bg-red-700 text-white">
              {busy === "add" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}{zh ? "保存" : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
